import React, { useEffect, useState } from 'react'
import ApplicationModel from '../../../../models/applications/api/ApplicationModel'
import { css } from '@emotion/css';
import { breakpoint_small } from '../../../../constants/breakpoints';
import { color_shades_dark, color_variants_shadow_10, dark_grey_blue_70 } from '../../../../constants/colors';
import styled from '@emotion/styled';
import Button from '../../../../components/general/Button';
import { connectFacebook, disconnectFacebook, getFacebookPages, subscribePages } from '../../../../api/controllers/meta';
import Select from 'react-select';
import { FacebookSubscribePagesRequest } from '../../../../models/features/api/Meta/FacebookSusbscribePagesRequest';
import Loader from '../../../../components/general/Loader';
import ConfirmationDialog from '../../../../components/structure/ConfirmationDialog';
import { FACEBOOK_CONFIG } from '../../../../constants/facebookIntegration';
const facebookIcon = require('../../../../content/images/facebook_Logo_Secondary.png');

interface Props {
    applications: ApplicationModel[],
    isLoading: boolean,
    organizationId: string
}

interface PageApplicationMapping {
    pageId: string;
    pageName: string;
    applicationId: string | null;
}

export default function Facebook(props: Props) {
    const [facebookExternalConnected, setFacebookExternalConnected] = useState(false);
    const [pageApplicationMappings, setPageApplicationMappings] = useState<PageApplicationMapping[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

    useEffect(() => {
        //@ts-ignore
        window.fbAsyncInit = () => {
            //@ts-ignore
            window.FB.init({
                appId: FACEBOOK_CONFIG.applicationId,
                xfbml: true,
                version: FACEBOOK_CONFIG.sdkVersion
            });
        };
        (function (d, s, id) {
            var js, fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) { return; }
            js = d.createElement(s); js.id = id;
            //@ts-ignore
            js.src = FACEBOOK_CONFIG.sdkUrl;
            //@ts-ignore
            fjs.parentNode.insertBefore(js, fjs);
        }(document, 'script', 'facebook-jssdk'));
    }, []);

    useEffect(() => {
        if (props.organizationId) {
            setIsLoading(true);
            getFacebookPages(props.organizationId).then(res => {
                setIsLoading(false)
                if (res && res.data) {
                    setFacebookExternalConnected(true);
                    setPageApplicationMappings(res.data.map(page => ({
                        pageId: page.id,
                        pageName: page.name,
                        applicationId: page.applicationId
                    })));
                }
            });
        }
    }, [props.organizationId]);

    const handleApplicationChange = (pageId: string, selectedOption: any) => {
        setPageApplicationMappings(prev =>
            prev.map(mapping =>
                mapping.pageId === pageId
                    ? { ...mapping, applicationId: selectedOption?.value || null }
                    : mapping
            )
        );
    };

    const handleSaveMappings = async () => {
        setIsSaving(true);
        try {
            const request: FacebookSubscribePagesRequest = {
                organizationId: props.organizationId,
                pageMappings: pageApplicationMappings.map(pm => ({
                    ApplicationId: pm.applicationId,
                    pageId: pm.pageId
                }))
            }
            var response = await subscribePages(request);
        } catch (error) {
            console.error('Error saving mappings:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDisconnect = async () => {
        setIsSaving(true);
        try {
            await disconnectFacebook(props.organizationId);
            setFacebookExternalConnected(false)
            setPageApplicationMappings(null);
        } catch {

        } finally {
            setIsSaving(false);
            setShowDisconnectDialog(false);
        }
    };

    const handleDisconnectClick = () => {
        setShowDisconnectDialog(true);
    };

    const handleCancelDisconnect = () => {
        setShowDisconnectDialog(false);
    };

    const applicationOptions = props.applications.map(app => ({
        value: app.id,
        label: app.name
    }));

    const facebookLogin = () => {
        //@ts-ignore
        window.FB.login(function (response) {
            if (response.authResponse) {

                if (!response.authResponse.code) {
                    //@ts-ignore
                    window.FB.logout()
                    return;
                }

                connectFacebook(props.organizationId, response.authResponse.code).then(res => {
                    if (res) {
                        setFacebookExternalConnected(true);
                        setPageApplicationMappings(res.data?.map(page => ({
                            pageId: page.id,
                            pageName: page.name,
                            applicationId: page.applicationId
                        })));
                    }
                });
            } else {
                console.log('User cancelled login or did not fully authorize.');
            }
        },
            {
                config_id: FACEBOOK_CONFIG.loginConfigId,
                response_type: 'code',
                override_default_response_type: true
            }
        );
    }

    function renderComponents() {
        if (isLoading) {
            return (
                <LoaderWrapper>
                    <StyledLoader />
                </LoaderWrapper>
            )
        }

        if (facebookExternalConnected) {
            return (
                <div className={connectedContentStyle}>
                    <div className={statusBadgeStyle}>
                        <div className={statusIndicatorStyle}></div>
                        <span>Connected to Facebook</span>
                    </div>
                    <p className={descriptionStyle}>
                        Map your Facebook pages to Voicify applications below. Each page can be connected to one application.
                    </p>
                    <div className={pagesContainerStyle}>
                        {pageApplicationMappings?.map((page, index) => {
                            const mapping = pageApplicationMappings.find(m => m.pageId === page.pageId);
                            return (
                                <div key={index} className={pageItemStyle}>
                                    <div className={pageInfoStyle}>
                                        <div className={pageNameStyle}>{mapping.pageName}</div>
                                        <div className={pageIdStyle}>ID: {mapping.pageId}</div>
                                    </div>
                                    <div className={selectWrapperStyle}>
                                        <Select
                                            className={selectStyle}
                                            classNamePrefix="select"
                                            options={applicationOptions}
                                            value={applicationOptions.find(opt => opt.value === mapping?.applicationId)}
                                            onChange={(option) => handleApplicationChange(mapping.pageId, option)}
                                            placeholder="Select an application..."
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )
        } else {
            return (
                <div className={disconnectedContentStyle}>
                    <button
                        onClick={facebookLogin}
                        className={facebookLoginButtonStyle}
                    >
                        <img src={facebookIcon} alt="Facebook" />
                        <span>Continue with Facebook</span>
                    </button>
                </div>
            )
        }
    }

    return (
        <div className={integrationCardStyle}>
            <div className={cardHeaderStyle}>
                <div className={titleSectionStyle}>
                    <div>
                        <h2>Facebook Integration</h2>
                        <p className={subtitleStyle}>Connect and manage your Facebook pages</p>
                    </div>
                </div>
                {facebookExternalConnected && (
                    <Button
                        themes={["destructive"]}
                        text="Disconnect"
                        loading={isSaving}
                        className={disconnectButtonStyle}
                        onClick={handleDisconnectClick}
                    />
                )}
            </div>

            <div className={cardContentStyle}>
                {renderComponents()}
            </div>

            {facebookExternalConnected && (
                <div className={cardFooterStyle}>
                    <Button
                        themes={["primary"]}
                        text="Save Changes"
                        loading={isSaving}
                        className={saveButtonStyle}
                        onClick={handleSaveMappings}
                    />
                </div>
            )}

            {showDisconnectDialog && (
                <ConfirmationDialog
                    onClose={handleCancelDisconnect}
                    title="Disconnect Facebook Integration"
                    deleteText="Disconnect"
                    onConfirm={handleDisconnect}
                    isLoading={isSaving}
                >
                    <div className={dialogContentStyle}>
                        <p>Are you sure you want to disconnect your Facebook integration?</p>
                        <p>This will remove all page mappings and you'll need to reconnect to manage your Facebook pages again.</p>
                    </div>
                </ConfirmationDialog>
            )}
        </div>
    );
};

const LoaderWrapper = styled.div`
    height: 100px;
`;

const StyledLoader = styled(Loader)`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const integrationCardStyle = css`
    border: 1px solid ${color_shades_dark};
    border-radius: 12px;
    background: white;
    box-shadow: 0 4px 12px 0 ${color_variants_shadow_10};
    margin-top: 16px;
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 6px 20px 0 ${color_variants_shadow_10};
    }

    ${breakpoint_small} {
        margin-top: 12px;
    }
`;

const cardHeaderStyle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 24px 16px;
    border-bottom: 1px solid #f0f0f0;

    ${breakpoint_small} {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const titleSectionStyle = css`
    display: flex;
    align-items: center;
    gap: 12px;

    h2 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #1a1a1a;
    }
`;


const subtitleStyle = css`
    margin: 4px 0 0 0;
    font-size: 14px;
    color: ${dark_grey_blue_70};
`;

const cardContentStyle = css`
    position: relative;
    padding: 14px 44px 14px 44px;
`;

const cardFooterStyle = css`
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
    background: #fafafa;
    display: flex;
    justify-content: flex-end;
`;

const connectedContentStyle = css`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const disconnectedContentStyle = css`
    padding: 32px 0;
`;

const connectPromptStyle = css`
    margin-bottom: 32px;

    h3 {
        margin: 0 0 8px 0;
        font-size: 18px;
        font-weight: 600;
        color: #1a1a1a;
    }

    p {
        margin: 0;
        font-size: 14px;
        color: ${dark_grey_blue_70};
        line-height: 1.5;
    }
`;

const statusBadgeStyle = css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f0f9ff;
    border: 1px solid #0ea5e9;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    color: #0ea5e9;
    width: fit-content;
`;

const statusIndicatorStyle = css`
    width: 8px;
    height: 8px;
    background: #10b981;
    border-radius: 50%;
`;

const descriptionStyle = css`
    margin: 0;
    font-size: 14px;
    color: ${dark_grey_blue_70};
    line-height: 1.5;
`;

const facebookLoginButtonStyle = css`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    background: #1877f2;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(24, 119, 242, 0.3);

    &:hover {
        background: #166fe5;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(24, 119, 242, 0.4);
    }

    &:active {
        transform: translateY(0);
    }

    img {
        width: 20px;
        height: 20px;
    }
`;

const pagesContainerStyle = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const pageItemStyle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fafafa;
    transition: all 0.2s ease;

    &:hover {
        border-color: #d1d5db;
        background: #f9fafb;
    }

    ${breakpoint_small} {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`;

const pageInfoStyle = css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1;
`;

const pageNameStyle = css`
    font-weight: 600;
    font-size: 16px;
    color: #1a1a1a;
    word-break: break-word;
`;

const pageIdStyle = css`
    font-size: 12px;
    color: ${dark_grey_blue_70};
    font-family: monospace;
`;

const selectWrapperStyle = css`
    min-width: 280px;
    
    ${breakpoint_small} {
        width: 100%;
        min-width: auto;
    }
`;

const selectStyle = css`
    font-size: 14px;
    
    .select__control {
        border: 1px solid #d1d5db;
        border-radius: 6px;
        min-height: 40px;
        box-shadow: none;
        transition: all 0.2s ease;
        
        &:hover {
            border-color: #9ca3af;
        }
        
        &--is-focused {
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
    }
    
    .select__value-container {
        padding: 8px 12px;
    }
    
    .select__placeholder {
        color: #6b7280;
    }
    
    .select__single-value {
        color: #1f2937;
    }
    
    .select__menu {
        border-radius: 6px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        border: 1px solid #e5e7eb;
    }
    
    .select__option {
        padding: 12px;
        
        &--is-selected {
            background-color: #3b82f6;
            color: white;
        }
        
        &--is-focused {
            background-color: #f3f4f6;
            color: #1f2937;
        }
        
        &:hover {
            background-color: #f3f4f6;
            color: #1f2937;
        }
    }
    
    .select__indicator-separator {
        background-color: #d1d5db;
    }
    
    .select__dropdown-indicator {
        color: #6b7280;
        
        &:hover {
            color: #374151;
        }
    }
`;

const disconnectButtonStyle = css`
    border-radius: 6px;
    font-size: 14px;
`;

const saveButtonStyle = css`
    font-size: 14px;
`;

const dialogContentStyle = css`
    padding: 16px;
`;